'use client';

import cx from 'classnames';
import { ReactNode, useEffect, useState } from 'react';
import { useCopyToClipboard } from 'react-use';

import CopyIcon from '@flipster/react/icon/generated/CopyIcon';

import { Spinner } from '../Spinner';
import CheckUnfilledIcon from '../assets/svgs/unfilled/check-unfilled.svg';
import styles from './textCopy.module.scss';

interface Props {
  isLoading?: boolean;
  shape?: 'default' | 'custom';
  text?: string;
  onClickCopy?: () => void;
  className?: string;
  children?: ReactNode;
}

export const TextCopy = ({
  isLoading,
  shape = 'default',
  text,
  onClickCopy: onClickCallback,
  className,
  children,
}: Props) => {
  const [copied, setCopied] = useState(false);
  const [, copyToClipboard] = useCopyToClipboard();

  useEffect(() => {
    if (!copied) return;
    const timeout = setTimeout(() => setCopied(false), 1000);
    return () => clearTimeout(timeout);
  }, [copied]);

  const onClickCopy = () => {
    if (!text) return;
    copyToClipboard(text);
    setCopied(true);
    onClickCallback?.();
  };

  if (isLoading)
    return (
      <div className={cx(styles.wrapper, styles[shape], styles.isLoading, className)}>
        <Spinner center />
      </div>
    );

  return (
    <button
      type='button'
      className={cx(styles.wrapper, styles[shape], className, { [styles.copied]: copied })}
      onClick={onClickCopy}
      translate='no'
    >
      {children ?? <p className={styles.text}>{text}</p>}
      <CopyIcon />
      <div className={styles.copiedWrapper}>
        <CheckUnfilledIcon />
        <span>Copied</span>
      </div>
    </button>
  );
};
